import { useRef, useState } from "react";
import { uploadPhoto, type PhotoSlot } from "../lib/api";

interface Props {
  itemId: string;
  slot: PhotoSlot;
  caption: string;
  /** Existing image to show until a new one is captured (replace flow). */
  existingUrl?: string | null;
  onUploaded?: (s3Key: string) => void;
}

/**
 * One tap opens the rear camera on iOS. The preview is a local object URL, so
 * the slot fills the instant the shutter closes rather than waiting on S3.
 */
export function CaptureSlot({ itemId, slot, caption, existingUrl, onUploaded }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setPreview(URL.createObjectURL(file));
    setProgress(0.01);

    try {
      const { s3Key } = await uploadPhoto(itemId, slot, file, setProgress);
      setProgress(1);
      onUploaded?.(s3Key);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
      setPreview(null);
      setProgress(0);
    }
  }

  const shown = preview ?? existingUrl ?? null;

  return (
    <div>
      <button
        type="button"
        className={`slot ${shown ? "filled" : ""}`}
        onClick={() => input.current?.click()}
        style={{ width: "100%", border: undefined }}
      >
        {shown && <img src={shown} alt="" />}
        <span className="slot-caption">{caption}</span>
        {progress > 0 && progress < 1 && (
          <span className="slot-progress" style={{ width: `${progress * 100}%` }} />
        )}
        {/* No `capture` attribute: iOS/Android then offer both the camera and
            the photo library. Add it back to force live capture only. */}
        <input
          ref={input}
          type="file"
          accept="image/*"
          onChange={handleFile}
        />
      </button>
      {error && <p className="hint" style={{ color: "var(--tag-alert)" }}>{error}</p>}
    </div>
  );
}
